import React, { useEffect } from 'react'
import { Modal, Steps, Form, Row, Col, InputNumber, Switch, Button, Empty, Tag, message, App } from 'antd'
import SelectForm from 'components/SelectForm/SelectForm'
import InputPrefix from 'components/InputPrefix/InputPrefix'
import { validarLongitudMaxMinde12 } from 'util/funciones'
import useConfiguracionInicial from '../hooks/useConfiguracionInicial'
import './index.css'

const STEPS = [
    { title: 'Información básica' },
    { title: 'Pagos y tiempos' },
    { title: 'Caras' },
]

const requiredRules = [{ required: true, message: 'Campo requerido' }]

const FIELDS_NAME = {
    IDENTIFICADOR: ['identificador'],
    NUMERO_PANTALLA: ['numero_pantalla'],
}

const ModalConfiguracionInicial: React.FC = () => {
    const {
        modalOpen,
        closeModal,
        step,
        form: ctxForm,
        errors,
        saving,
        setField,
        next,
        save,
        datafonos,
        surtidoresDetalles,
        toggleDetalle,
        isDetalleSeleccionado,
        organizaciones,
        edsOptions,
        equipos,
        loadingEds,
        loadingEquipos,
        onChangeOrganizacion,
        onChangeEds,
        readOnly
    } = useConfiguracionInicial()
    const { notification } = App.useApp()

    const [form] = Form.useForm()

    useEffect(() => {
        if (!modalOpen) return
        form.setFieldsValue({
            [FIELDS_NAME.IDENTIFICADOR[0]]: ctxForm.identificador,
            [FIELDS_NAME.NUMERO_PANTALLA[0]]: ctxForm.numero_pantalla ?? undefined,
        })
    }, [modalOpen, ctxForm.identificador, ctxForm.numero_pantalla])

    const handleNext = async () => {
        if (step === 1 && !readOnly) {
            try {
                await form.validateFields(FIELDS_NAME.IDENTIFICADOR.concat(FIELDS_NAME.NUMERO_PANTALLA))
            } catch {
                return
            }
        }
        next()
    }

    const handleClose = () => {
        form.resetFields()
        closeModal()
    }

   const handleSave = async () => {
        try {
            await form.validateFields()
        } catch {
            notification.warning({
                message: 'Advertencia',
                description: 'Revisa los campos requeridos.'
            })
            return
        }

        const result = await save()
        if (!result.success) {
            notification.error({
                message: 'Error',
                description: result.message ?? 'No se pudo guardar la configuración.'
            })
        }else {
            notification.success({
                message: 'Éxito',
                description: result.message ?? 'Operación realizada correctamente.'
            })
        }
        
    }

    return (
        <Modal
            open={modalOpen}
            onCancel={handleClose}
            footer={null}
            centered
            width={640}
            destroyOnClose
            title={
                <span className="mci-title">
                    {readOnly ? 'Información de la configuración' : ctxForm.id ? 'Editar configuración' : 'Nueva configuración POS'}
                </span>
            }
        >
            <div className="mci-root">
                <Steps current={step - 1} items={STEPS} size="small" className="mci-steps" />

                <Form form={form} layout="vertical" style={{ width: '100%' }}>
                    <div className="mci-body">
                        {/* ---------- Información básica ---------- */}
                        {step === 1 && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <label className="mci-label-block">Organización*</label>
                                    <SelectForm
                                        style={{ width: '100%' }}
                                        placeholder="Seleccione una organización"
                                        options={organizaciones}
                                        value={ctxForm.organizacion_id || undefined}
                                        onChange={onChangeOrganizacion}
                                        disabled={!!ctxForm.id}
                                    />
                                    {errors.organizacion_id && <span className="mci-error">{errors.organizacion_id}</span>}
                                </Col>

                                {ctxForm.organizacion_id && (
                                    <Col span={24}>
                                        <label className="mci-label-block">EDS*</label>
                                        <SelectForm
                                            style={{ width: '100%' }}
                                            placeholder="Seleccione una EDS"
                                            options={edsOptions}
                                            loading={loadingEds}
                                            value={ctxForm.eds_id || undefined}
                                            onChange={onChangeEds}
                                            disabled={!!ctxForm.id}
                                        />
                                        {errors.eds_id && <span className="mci-error">{errors.eds_id}</span>}
                                    </Col>
                                )}

                                {ctxForm.eds_id && (
                                    <Col span={24}>
                                        <label className="mci-label-block">Equipo*</label>
                                        <SelectForm
                                            style={{ width: '100%' }}
                                            placeholder="Seleccione un equipo"
                                            loading={loadingEquipos}
                                            options={equipos.map((eq) => ({ label: eq.nombre, value: eq.id }))}
                                            value={ctxForm.equipo_id || undefined}
                                            onChange={(value: any) => setField('equipo_id', value)}
                                            disabled={!!ctxForm.id}
                                        />
                                        {errors.equipo_id && <span className="mci-error">{errors.equipo_id}</span>}
                                    </Col>
                                )}

                                <Col span={24} className="mci-col-spaced">
                                    <InputPrefix
                                        name={FIELDS_NAME.IDENTIFICADOR[0]}
                                        prefix="Identificador POS*"
                                        placeholder="000000000001"
                                        className="select-gray"
                                        value={ctxForm.identificador}
                                        onChange={(e: any) => setField('identificador', e.target.value)}
                                        rules={[...requiredRules, { validator: validarLongitudMaxMinde12()},]}
                                        maxLength={12}
                                        minLength={12}
                                        disabled={!!ctxForm.id}
                                    />
                                    {errors.identificador && <span className="mci-error">{errors.identificador}</span>}
                                </Col>

                                <Col span={24}>
                                    <InputPrefix
                                        name={FIELDS_NAME.NUMERO_PANTALLA[0]}
                                        prefix="Número de pantalla*"
                                        placeholder="1"
                                        className="select-gray"
                                        value={ctxForm.numero_pantalla ?? ''}
                                        onChange={(e: any) =>
                                            setField('numero_pantalla', e.target.value === '' ? null : Number(e.target.value))
                                        }
                                        rules={requiredRules}
                                        maxLength={6}
                                        disabled={readOnly}
                                    />
                                    {errors.numero_pantalla && <span className="mci-error">{errors.numero_pantalla}</span>}
                                </Col>
                            </Row>
                        )}

                        {/* ---------- Pagos y tiempos ---------- */}
                        {step === 2 && (
                            <Row gutter={16}>
                                <Col span={24} className="mci-switch-row">
                                    <span className="mci-label-block">Permitir pago en efectivo</span>
                                    <Switch
                                        checked={ctxForm.permitir_efectivo}
                                        onChange={(checked) => setField('permitir_efectivo', checked)}
                                        disabled={readOnly}
                                    />
                                    <span className="mci-switch-text">
                                        {ctxForm.permitir_efectivo ? 'Habilitado' : 'Deshabilitado'}
                                    </span>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <label className="mci-label-block">Datáfono</label>
                                    <SelectForm
                                        style={{ width: '100%' }}
                                        allowClear
                                        placeholder="Seleccione un datáfono…"
                                        options={datafonos.map((d) => ({ label: d.nombre, value: d.id }))}
                                        value={ctxForm.datafono_id || undefined}
                                        onChange={(v) => setField('datafono_id', v)}
                                        disabled={readOnly}
                                    />
                                </Col>

                                <Col xs={24} sm={12}>
                                    <label className="mci-label-block">Inactividad (segundos)</label>
                                    <InputNumber
                                        className="mci-input"
                                        style={{ width: '100%' }}
                                        addonAfter="seg"
                                        value={Number(ctxForm.inactividad_segundos)}
                                        onChange={(v) => setField('inactividad_segundos', v ?? 0)}
                                        disabled={readOnly}
                                    />
                                </Col>

                                <Col xs={24} sm={12}>
                                    <label className="mci-label-block">Tiempo de espera de pago</label>
                                    <InputNumber
                                        className="mci-input"
                                        style={{ width: '100%' }}
                                        addonAfter="seg"
                                        value={Number(ctxForm.tiempo_espera_pago)}
                                        onChange={(v) => setField('tiempo_espera_pago', v ?? 0)}
                                        disabled={readOnly}
                                    />
                                </Col>

                                <Col xs={24} sm={12}>
                                    <label className="mci-label-block">Monto mínimo de programación</label>
                                    <InputNumber
                                        className="mci-input"
                                        style={{ width: '100%' }}
                                        addonBefore="$"
                                        addonAfter="COP"
                                        placeholder="0"
                                        value={ctxForm.monto_minimo_programacion ?? undefined}
                                        onChange={(v) => setField('monto_minimo_programacion', v ?? 0)}
                                        disabled={readOnly}
                                    />
                                </Col>
                            </Row>
                        )}

                        {/* ---------- Surtidores ---------- */}
                        {step === 3 && (
                            <div className="mci-mapeo">
                                <div className="mci-mapeo-head">
                                    <span>Surtidores seleccionados ({ctxForm.detalle_ids.length})</span>
                                </div>

                                {errors.detalle_ids && (
                                    <span className="mci-error" style={{ display: 'block', marginBottom: 10 }}>
                                        {errors.detalle_ids}
                                    </span>
                                )}

                                {surtidoresDetalles.length === 0 ? (
                                    <Empty
                                        description="No hay surtidores/mangueras disponibles para el equipo seleccionado."
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                ) : (
                                    <Row gutter={[10, 10]} className="mci-detalle-grid">
                                        {surtidoresDetalles.map((detalle) => {
                                            const seleccionado = isDetalleSeleccionado(detalle.id)
                                            return (
                                                <Col span={12} key={detalle.id}>
                                                    <button
                                                        type="button"
                                                        className={seleccionado ? 'mci-detalle-card active' : 'mci-detalle-card'}
                                                        onClick={() => toggleDetalle(detalle.id)}
                                                        disabled={readOnly}
                                                    >
                                                        <span>{detalle.nombre}</span>
                                                        {seleccionado && <Tag className="mci-tag-gold">Seleccionado</Tag>}
                                                    </button>
                                                </Col>
                                            )
                                        })}
                                    </Row>
                                )}
                            </div>
                        )}
                    </div>
                </Form>
                
                <div className="mci-footer">
                    {step < 3 ? (
                        <Button className="mci-btn-gold" onClick={handleNext}>
                            Siguiente
                        </Button>
                    ) : readOnly ? (
                        <Button className="mci-btn-gold" onClick={handleClose}>
                            Cerrar
                        </Button>
                    ) : (
                        <Button className="mci-btn-gold" onClick={handleSave} loading={saving}>
                            {ctxForm.id ? 'Editar configuración' : 'Guardar configuración'}
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    )
}

export default ModalConfiguracionInicial